import { Suspense, useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation, useParams } from 'react-router-dom';
import { QuickActionProvider } from '../contexts/QuickActionContext';
import { LotsProvider } from '../contexts/LotsContext';
import { SalesProvider } from '../contexts/SalesContext';
import { ClientsProvider } from '../contexts/ClientsContext';
import { AuthProvider, useAuth } from '../contexts/AuthContext.jsx';
import { BusinessProvider } from '../contexts/BusinessContext';
import { FinancialProvider } from '../contexts/FinancialContext';
import MobileLayout from '../components/layout/MobileLayout';
import NewLotForm from '../features/lots/components/forms/NewLotForm';
import LotDetail from '../features/lots/components/detail/LotDetail';
import GeneralSettings from '../features/settings/components/sections/GeneralSettings';
import PrinterManager from '../features/printers/components/PrinterManager';
import { TicketSettings } from '../features/settings/components/sections/TicketSettings';
import LotsSelection from '../features/sales/create/components/steps/LotsSelection';
import ReviewSale from '../features/sales/create/components/steps/ReviewSale';
import SuccessScreen from '../features/sales/create/components/steps/SuccessScreen';
import SaleDetail from '../features/sales/detail/SaleDetail';
import { useSaleCreation } from '../features/sales/create/hooks/useSaleCreation';
import LoginPage from '../features/auth/components/LoginPage';
import AuthCallback from '../features/auth/components/AuthCallback';
import { InviteSignup } from '../features/auth/components/InviteSignup';
import ProfileScreen from '../features/settings/components/ProfileScreen';
import CreateBusiness from '../components/business/CreateBusiness';
import CreateBusiness2 from '../components/business/CreateBusiness2';
import BusinessList from '../components/business/BusinessList';
import { PendingDeliveriesScreen } from '../features/dashboard/components/pending/PendingDeliveriesScreen';
import { MenuScreen } from '../features/menu/components/MenuScreen';
import { SettingsHub } from '../features/settings/components/SettingsHub';
import { ThemeProvider } from '../contexts/ThemeContext';
import { CajaScreen } from '../features/caja/components/CajaScreen';
import { CajasAnterioresScreen } from '../features/caja/components/CajasAnterioresScreen';
import { CajaHistorialDetail } from '../features/caja/components/CajaHistorialDetail';
import { ClientsScreen } from '../features/clients/components/ClientsScreen';
import { ForumPostList } from '../features/dashboard/components/forum/ForumPostList';
import { ForumPostForm } from '../features/dashboard/components/forum/ForumPostForm';
import { CatalogScreen } from '../features/dashboard/components/catalog/CatalogScreen';
import { StorePage } from '../features/store/StorePage';
import { TeamScreen } from '../features/team/components/TeamScreen';
import { SubscriptionManagement } from '../features/subscription/components';
import { Toaster, toast } from 'sonner';
import { Building2 } from 'lucide-react';
import { supabase } from '../services/supabase';

// Componente placeholder para la página de invitación
const JoinBusinessScreen = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const handleJoinRequest = async () => {
      // Evitar procesamiento múltiple
      if (isProcessing) return;
      if (!businessId) {
        setError('ID de negocio no válido');
        setStatus('error');
        return;
      }

      try {
        setIsProcessing(true);
        setStatus('checking');
        console.log('Checking business:', businessId);
        
        // 1. Verificar si el business existe
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('id, name, owner_id')
          .eq('id', businessId)
          .single();

        if (businessError) {
          console.error('Business error:', businessError);
          throw businessError;
        }
        
        console.log('Business found:', businessData);
        setBusiness(businessData);

        // 2. Si el usuario no está autenticado, redirigir a login
        if (!user) {
          console.log('User not authenticated, redirecting to login');
          localStorage.setItem('pendingJoinBusinessId', businessId);
          setStatus('redirecting');
          setTimeout(() => navigate('/login'), 1500);
          return;
        }

        console.log('User authenticated:', user.id);
        setStatus('checking');

        // 3. Verificar si ya es miembro
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('auth_user_id', user.id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Profile check error:', profileError);
          throw profileError;
        }

        if (profile) {
          // Verificar si ya es miembro del negocio
          const { data: membership, error: membershipError } = await supabase
            .from('business_members')
            .select('id')
            .eq('business_id', businessId)
            .eq('profile_id', profile.id)
            .single();

          if (membershipError && membershipError.code !== 'PGRST116') {
            console.error('Membership check error:', membershipError);
            throw membershipError;
          }

          if (membership) {
            console.log('User is already a member');
            setError('Ya eres miembro de este negocio');
            setStatus('error');
            setTimeout(() => navigate('/'), 2000);
            return;
          }
        }

        // 4. Verificar si ya tiene una solicitud pendiente
        const { data: existingRequest, error: requestCheckError } = await supabase
          .from('business_join_requests')
          .select('id, status')
          .eq('business_id', businessId)
          .eq('user_id', user.id)
          .single();

        if (requestCheckError && requestCheckError.code !== 'PGRST116') {
          console.error('Request check error:', requestCheckError);
          throw requestCheckError;
        }

        if (existingRequest) {
          if (existingRequest.status === 'pending') {
            setError('Ya tienes una solicitud pendiente');
            setStatus('error');
          } else if (existingRequest.status === 'rejected') {
            setError('Tu solicitud fue rechazada');
            setStatus('error');
          }
          setTimeout(() => navigate('/'), 2000);
          return;
        }

        // 5. Crear solicitud de unión
        setStatus('requesting');
        console.log('Creating join request');
        
        const { error: requestError } = await supabase
          .from('business_join_requests')
          .insert([{
            business_id: businessId,
            user_id: user.id,
            status: 'pending',
            user_email: user.email,
            user_name: user.user_metadata?.name || user.email.split('@')[0]
          }]);

        if (requestError) {
          console.error('Request creation error:', requestError);
          throw requestError;
        }

        // 6. Mostrar mensaje de éxito
        console.log('Join request created successfully');
        setStatus('success');
        toast.success('Solicitud enviada exitosamente');
        
        // Esperar un momento antes de redirigir
        await new Promise(resolve => setTimeout(resolve, 2000));
        navigate('/');

      } catch (error) {
        console.error('Error processing join request:', error);
        setError(error.message);
        setStatus('error');
        setTimeout(() => navigate('/'), 2000);
      } finally {
        setIsProcessing(false);
      }
    };

    // Solo procesar si no estamos ya procesando
    if (!isProcessing) {
      handleJoinRequest();
    }
  }, [businessId, user, navigate, isProcessing]);

  const statusMessages = {
    loading: 'Cargando...',
    checking: 'Verificando negocio...',
    redirecting: 'Redirigiendo al login...',
    requesting: 'Enviando solicitud...',
    success: '¡Solicitud enviada!',
    error: error || 'Error al procesar la solicitud'
  };

  const statusColors = {
    loading: 'bg-gray-500',
    checking: 'bg-blue-500',
    redirecting: 'bg-yellow-500',
    requesting: 'bg-[#3B7C5B]',
    success: 'bg-green-500',
    error: 'bg-red-500'
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
        <div className={`w-16 h-16 ${statusColors[status]} rounded-full mx-auto mb-4 flex items-center justify-center`}>
          <Building2 className="w-8 h-8 text-white" />
        </div>
        
        {business ? (
          <>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {business.name}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Has sido invitado a unirte al equipo de {business.name}
            </p>
          </>
        ) : (
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mx-auto mb-3 animate-pulse" />
        )}

        <p className={`text-sm ${status === 'error' ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
          {statusMessages[status]}
        </p>

        {status === 'error' && (
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 text-sm font-medium text-white bg-gray-500 hover:bg-gray-600 rounded-lg transition-colors"
          >
            Volver al inicio
          </button>
        )}
      </div>
    </div>
  );
};

// Componente de protección de rutas
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Cargando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Componente de redirección para usuarios autenticados
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Cargando...</div>;
  }

  if (user) {
    return <Navigate to={location.state?.from?.pathname || '/'} replace />;
  }

  return children;
};

// Componente interno que usa los hooks
function AppRoutes() {
  const {
    saleInProgress,
    handleSaleItemAdded,
    handleEditItem,
    handleDeleteItem,
    handleSelectClient,
    handleSaleConfirmed
  } = useSaleCreation();

  const navigate = useNavigate();

  return (
    <Routes>
      {/* Rutas públicas */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />

      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Ruta de invitación - Usando el nuevo componente dedicado */}
      <Route path="/i/:businessId" element={<InviteSignup />} />

      {/* Ruta pública para el catálogo */}
      <Route path="/c/:businessId" element={<StorePage />} />
      
      {/* Nueva ruta amigable para el catálogo */}
      <Route path="/catalogo/:businessSlug" element={<StorePage />} />

      {/* Rutas protegidas */}
      <Route path="/" element={
        <ProtectedRoute>
          <MobileLayout />
        </ProtectedRoute>
      } />
      
      <Route path="/sales" element={
        <ProtectedRoute>
          <MobileLayout initialTab="sales" />
        </ProtectedRoute>
      } />

      <Route path="/lots/new" element={
        <ProtectedRoute>
          <NewLotForm />
        </ProtectedRoute>
      } />

      <Route path="/lots/:id" element={
        <ProtectedRoute>
          <LotDetail />
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsHub />
        </ProtectedRoute>
      } />

      <Route path="/settings/general" element={
        <ProtectedRoute>
          <GeneralSettings />
        </ProtectedRoute>
      } />

      <Route path="/settings/printers" element={
        <ProtectedRoute>
          <PrinterManager />
        </ProtectedRoute>
      } />

      <Route path="/settings/tickets" element={
        <ProtectedRoute>
          <TicketSettings />
        </ProtectedRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfileScreen />
        </ProtectedRoute>
      } />

      <Route path="/sales/new" element={
        <ProtectedRoute>
          <LotsSelection 
            onBack={() => navigate('/')}
            onSaleConfirmed={handleSaleItemAdded}
          />
        </ProtectedRoute>
      } />

      <Route path="/sales/review" element={
        <ProtectedRoute>
          <ReviewSale 
            items={saleInProgress.items}
            client={saleInProgress.client}
            onBack={() => navigate('/sales/new')}
            onAddMore={() => navigate('/sales/new')}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            onSelectClient={handleSelectClient}
            onConfirm={handleSaleConfirmed}
          />
        </ProtectedRoute>
      } />

      <Route path="/sales/success/:id" element={
        <ProtectedRoute>
          <SuccessScreen 
            onClose={() => navigate('/sales')}
            onViewDetails={(id) => navigate(`/sales/${id}`)}
          />
        </ProtectedRoute>
      } />

      <Route path="/sales/:id" element={
        <ProtectedRoute>
          <SaleDetail />
        </ProtectedRoute>
      } />

      {/* Pending Deliveries Route */}
      <Route path="/pending-deliveries" element={
        <ProtectedRoute>
          <PendingDeliveriesScreen />
        </ProtectedRoute>
      } />

      {/* Menu Route */}
      <Route path="/menu" element={
        <ProtectedRoute>
          <MobileLayout initialTab="menu" />
        </ProtectedRoute>
      } />

      {/* Caja Route */}
      <Route path="/caja" element={
        <ProtectedRoute>
          <CajaScreen />
        </ProtectedRoute>
      } />
      
      {/* Historial de Cajas */}
      <Route path="/caja/historial" element={
        <ProtectedRoute>
          <CajasAnterioresScreen />
        </ProtectedRoute>
      } />
      
      {/* Detalle de Caja Histórica */}
      <Route path="/caja/historial/:closingId" element={
        <ProtectedRoute>
          <CajaHistorialDetail />
        </ProtectedRoute>
      } />

      {/* Clients Route */}
      <Route path="/clients" element={
        <ProtectedRoute>
          <ClientsScreen />
        </ProtectedRoute>
      } />

      {/* Team Route */}
      <Route path="/team" element={
        <ProtectedRoute>
          <TeamScreen />
        </ProtectedRoute>
      } />

      {/* Business Routes */}
      <Route path="/businesses" element={
        <ProtectedRoute>
          <BusinessList />
        </ProtectedRoute>
      } />
      
      <Route path="/create-business-1" element={
        <ProtectedRoute>
          <CreateBusiness onBusinessCreated={() => window.location.reload()} />
        </ProtectedRoute>
      } />
      
      <Route path="/create-business-2" element={
        <ProtectedRoute>
          <CreateBusiness2 />
        </ProtectedRoute>
      } />

      {/* Forum Routes */}
      <Route path="/forum" element={
        <ProtectedRoute>
          <ForumPostList />
        </ProtectedRoute>
      } />
      
      <Route path="/forum/new" element={
        <ProtectedRoute>
          <ForumPostForm />
        </ProtectedRoute>
      } />

      {/* Catalog Routes */}
      <Route path="/catalog" element={
        <ProtectedRoute>
          <CatalogScreen />
        </ProtectedRoute>
      } />

      {/* Subscription Route */}
      <Route path="/subscription" element={
        <ProtectedRoute>
          <SubscriptionManagement />
        </ProtectedRoute>
      } />

      {/* Ruta por defecto */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Componente principal que provee el contexto
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BusinessProvider>
          <FinancialProvider>
            <QuickActionProvider>
              <LotsProvider>
                <SalesProvider>
                  <ClientsProvider>
                    <Suspense fallback={<div>Loading...</div>}>
                      <AppRoutes />
                      <Toaster richColors position="top-center" />
                    </Suspense>
                  </ClientsProvider>
                </SalesProvider>
              </LotsProvider>
            </QuickActionProvider>
          </FinancialProvider>
        </BusinessProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 